import ctl from '@netlify/classnames-template-literals'
import { ReactNode } from 'react'
import { useSafeZone } from '../canvas-store'
import type { ContentXPin, ContentYPin, Spacing, TextAlignMode, TextAlignValue, TextStyle } from '../data/v1'
import { useFontFamilyStyle } from '../lib/use-font-family-style'
import { useResolvedColor } from '../lib/use-resolved-color'

export type ContentLayerRendererProps = {
	layerId: string
	xPin: ContentXPin
	yPin: ContentYPin
	textAlignMode: TextAlignMode
	textAlignValue: TextAlignValue
	spacing: Spacing
	gap: number
	textStyle: TextStyle
	children?: ReactNode
}

const xPinStyles: Record<ContentXPin, string> = {
	left: 'items-start',
	center: 'items-center',
	right: 'items-end',
	stretch: 'items-stretch',
}

const yPinStyles: Record<ContentYPin, string> = {
	top: 'justify-end',
	center: 'justify-center',
	bottom: 'justify-start',
}

export function ContentLayerRenderer(
	{ layerId, xPin, yPin, textAlignMode, textAlignValue, spacing, gap, textStyle, children }:
		ContentLayerRendererProps,
) {
	// Safe zone comes from context, not an atom, so inset changes land in the
	// same commit as the canvas resize (see SafeZoneContext in canvas-store.tsx).
	const safeZone = useSafeZone()
	const resolvedColor = useResolvedColor(textStyle.textColor)
	const { style: fontFamilyStyle } = useFontFamilyStyle(
		textStyle.fontFamilyMode,
		textStyle.fontFamilyProvider,
		textStyle.fontFamilyGoogleFamily,
		textStyle.fontFamilyCustomUrl,
		textStyle.fontFamilyCustomName,
	)

	return (
		<div
			data-layer-id={layerId}
			className={ctl(`absolute flex flex-col-reverse ${xPinStyles[xPin]} ${yPinStyles[yPin]}`)}
			style={{
				pointerEvents: 'none',
				...(textAlignMode !== 'auto' && { textAlign: textAlignValue }),
				...fontFamilyStyle,
				color: textStyle.textColorMode !== 'auto' ? resolvedColor : undefined,
				...(textStyle.fontSizeMode === 'value' && { fontSize: textStyle.fontSizePxValue }),
				...(textStyle.fontWeightMode === 'value' && { fontWeight: textStyle.fontWeightValue }),
				...(textStyle.fontStyleMode !== 'auto' && { fontStyle: textStyle.fontStyleValue }),
				...(textStyle.lineHeightMode === 'value' && { lineHeight: textStyle.lineHeightValue }),
				...(textStyle.textShadowMode === 'value' && textStyle.textShadowValue === 'none'
					&& { textShadow: 'none' }),
				...(textStyle.textShadowMode === 'value' && textStyle.textShadowValue === 'simple' && {
					textShadow: `0 0 ${textStyle.textShadowSimple.spread}px rgba(0,0,0,${
						textStyle.textShadowSimple.opacity / 100
					})`,
				}),
				gap,
				paddingTop: spacing.paddingTop,
				paddingBottom: spacing.paddingBottom,
				paddingLeft: spacing.paddingLeft,
				paddingRight: spacing.paddingRight,
				// Expressed as insets, not inset-0 + margin: Chrome ≥150 fails to
				// relayout this absolutely-positioned auto-height box when only its
				// margin changes (see "Safe zones" in README.md). Setting top/right/
				// bottom/left directly avoids that code path.
				top: spacing.marginTop + safeZone.top,
				right: spacing.marginRight + safeZone.right,
				bottom: spacing.marginBottom + safeZone.bottom,
				left: spacing.marginLeft + safeZone.left,
			}}
		>
			{children}
		</div>
	)
}
